// Bloque «La Central» del tab Venta: estado de los avisos a Zentral y la acción «Reenviar».
import { useTranslation } from 'react-i18next';
import { RotateCw } from 'lucide-react';
import { Button } from '@/kit';
import { ApiError } from '@/api/errors';
import type { BackofficeSale, CentralResendKind } from '@/api/services/retail-wizard/contract';
import { StatusPill } from '@/shared/patterns/Pills';
import type { Translate } from '@/shared/patterns/CancelSale';
import {
    CENTRAL_CUSTOMER_PHONE_INVALID,
    centralStatusView,
    hasCentralPieceConflict,
    RESENDABLE,
    resendableKinds,
} from '../../lib/centralStatus';
import { CENTRAL_SYNC_NOT_RESENDABLE, useResendCentral } from '../../api/useVentas';
import { InfoRow, TabNote, TabSection } from './VentaTab';

type CentralSectionSale = Pick<
    BackofficeSale,
    'saleId' | 'status' | 'centralSyncStatus' | 'centralCancelStatus' | 'centralLastErrorCode'
>;

function resendErrorMessage(error: unknown, t: Translate): string {
    if (error instanceof ApiError && error.code === CENTRAL_SYNC_NOT_RESENDABLE) {
        return t('central.errores.noReenviable', {
            defaultValue:
                'Este aviso ya no se puede reenviar. Recargamos la venta con su estado actual.',
        });
    }
    if (error instanceof ApiError && error.code === 'SALE_NOT_FOUND') {
        return t('errores.ventaNoEncontrada', {
            defaultValue:
                'No encontramos esta venta en tu cadena. Actualiza la lista y vuelve a buscarla.',
        });
    }
    return t('central.errores.generico', {
        defaultValue: 'No pudimos reenviar el aviso. Revisa tu conexión e intenta de nuevo.',
    });
}

function StatusValue({ status, t }: { status: string | null | undefined; t: Translate }) {
    const view = centralStatusView(status);
    return (
        <StatusPill tone={view.tone}>
            {t(`central.estado.${view.key}`, { defaultValue: view.defaultLabel })}
        </StatusPill>
    );
}

/** Monta la mutación aparte: sin avisos que reenviar no se pide QueryClient. */
function CentralResendActions({
    saleId,
    kinds,
    t,
}: {
    saleId: string;
    kinds: CentralResendKind[];
    t: Translate;
}) {
    const mutation = useResendCentral();
    const resendLabel: Record<CentralResendKind, string> = {
        SALE_REGISTER: t('central.reenviarRegistro', { defaultValue: 'Reenviar registro' }),
        SALE_CANCEL: t('central.reenviarCancelacion', { defaultValue: 'Reenviar cancelación' }),
    };
    return (
        <>
            <div className="mt-2 flex flex-wrap gap-2">
                {kinds.map((kind) => (
                    <Button
                        key={kind}
                        type="button"
                        variant="outline"
                        size="sm"
                        leftIcon={<RotateCw aria-hidden />}
                        isLoading={mutation.isPending && mutation.variables.kind === kind}
                        disabled={mutation.isPending}
                        onClick={() => {
                            mutation.mutate({ saleId, kind });
                        }}
                    >
                        {resendLabel[kind]}
                    </Button>
                ))}
            </div>
            {mutation.isSuccess ? (
                <TabNote variant="success">
                    {t('central.reenviado', {
                        defaultValue:
                            'Listo. El aviso volvió a la fila y se enviará en los próximos minutos.',
                    })}
                </TabNote>
            ) : null}
            {mutation.isError ? (
                <TabNote variant="danger">{resendErrorMessage(mutation.error, t)}</TabNote>
            ) : null}
        </>
    );
}

/** Aviso del rechazo por la pieza; si la venta ya se canceló, no hay nada que cambiar. */
function PieceConflictNote({ cancelled, t }: { cancelled: boolean; t: Translate }) {
    if (cancelled) {
        return (
            <TabNote variant="info">
                {t('central.piezaVentaCancelada', {
                    defaultValue:
                        'La Central tiene la pieza de esta venta y la venta ya está cancelada: no hay nada que reenviar ni cambiar.',
                })}
            </TabNote>
        );
    }
    return (
        <TabNote variant="danger">
            {t('central.piezaNoDisponible', {
                defaultValue:
                    'La Central ya no tiene disponible la pieza de esta venta. Reenviar no lo resuelve: cambia la pieza o cancela la venta.',
            })}
        </TabNote>
    );
}

/** Solo aparece en ventas de una cadena integrada: sin aviso de registro no hay nada que mostrar. */
export function CentralSection({ sale }: { sale: CentralSectionSale }) {
    const { t } = useTranslation('ventas');
    if (centralStatusView(sale.centralSyncStatus).key === 'N_A') return null;

    const kinds = resendableKinds(sale);
    const rejected =
        RESENDABLE.has(sale.centralSyncStatus) || RESENDABLE.has(sale.centralCancelStatus ?? '');

    return (
        <TabSection title={t('central.titulo', { defaultValue: 'La Central' })}>
            <InfoRow
                label={t('central.registro', { defaultValue: 'Registro' })}
                value={<StatusValue status={sale.centralSyncStatus} t={t} />}
            />
            {sale.centralCancelStatus ? (
                <InfoRow
                    label={t('central.cancelacion', { defaultValue: 'Cancelación' })}
                    value={<StatusValue status={sale.centralCancelStatus} t={t} />}
                />
            ) : null}
            {rejected && sale.centralLastErrorCode ? (
                <InfoRow
                    label={t('central.codigo', { defaultValue: 'Código' })}
                    value={sale.centralLastErrorCode}
                    mono
                />
            ) : null}
            {sale.centralLastErrorCode === CENTRAL_CUSTOMER_PHONE_INVALID ? (
                <TabNote variant="danger">
                    {t('central.telefonoInvalido', {
                        defaultValue:
                            'El teléfono del cliente no tiene 10 dígitos, así que no pudimos avisar la venta a La Central. Corrige el teléfono del cliente y reenvía el aviso.',
                    })}
                </TabNote>
            ) : null}
            {hasCentralPieceConflict(sale) ? (
                <PieceConflictNote cancelled={sale.status === 'CANCELLED'} t={t} />
            ) : null}
            {kinds.length > 0 ? (
                <CentralResendActions saleId={sale.saleId} kinds={kinds} t={t} />
            ) : null}
        </TabSection>
    );
}
